import axios from "../utils/index";
import type { HospitalAutomation } from "../features/automation/types";

export type AutomationIssuePack = HospitalAutomation & {
  issues?: string[];
  valid?: boolean;
};

export type AutomationRun = {
  runId: string;
  automationId: string;
  name?: string;
  triggerKind?: string;
  shiftId?: string;
  ordersId?: string;
  status?: string;
  currentNodeId?: string;
  nextWakeAt?: number;
  lastIssue?: string;
  createdAt?: number;
  updatedAt?: number;
  events?: Array<{
    eventId: string;
    createdAt: number;
    level: string;
    code: string;
    message: string;
    nodeId?: string;
  }>;
};

export type AutomationExportPack = {
  format?: string;
  automation: HospitalAutomation;
};

const dataOf = <T>(response: { data?: { code?: number; data?: T; message?: string } }) => {
  if (response.data?.code === 200 || response.data?.code === 0) {
    return response.data.data as T;
  }
  throw new Error(response.data?.message || "Automation request failed.");
};

export const listHospitalAutomations = async (): Promise<AutomationIssuePack[]> => {
  const response = await axios.get("/v1/hospital/automations");
  return dataOf<AutomationIssuePack[]>(response) || [];
};

export const upsertHospitalAutomation = async (
  automation: HospitalAutomation,
): Promise<AutomationIssuePack> => {
  const response = await axios.put("/v1/hospital/automations", automation);
  return dataOf<AutomationIssuePack>(response);
};

export type AutomationTestStep = {
  nodeId?: string;
  kind?: string;
  label?: string;
  branch?: string;
  code?: string;
  description?: string;
  mutates?: boolean;
};

export type AutomationTestResult = {
  safe?: boolean;
  valid?: boolean;
  wouldStart?: boolean;
  trigger?: string;
  summary?: string;
  skipReason?: string;
  scenario?: string;
  issues?: string[];
  steps?: AutomationTestStep[];
};

export const testHospitalAutomation = async (payload: {
  automation: HospitalAutomation;
  shiftId?: string;
  doctorId?: string;
  grouped?: boolean;
  daysUntilStart?: number;
  confirmed?: boolean;
  precredentialed?: boolean;
  internal?: boolean;
}): Promise<AutomationTestResult> => {
  const response = await axios.post("/v1/hospital/automations/test", payload);
  return dataOf<AutomationTestResult>(response);
};

export const validateHospitalAutomation = async (
  automation: HospitalAutomation,
): Promise<AutomationIssuePack> => {
  const response = await axios.post("/v1/hospital/automations/validate", automation);
  return dataOf<AutomationIssuePack>(response);
};

export const setHospitalAutomationEnabled = async (
  id: string,
  enabled: boolean,
): Promise<AutomationIssuePack> => {
  const response = await axios.put(
    `/v1/hospital/automations/${encodeURIComponent(id)}/enabled`,
    null,
    { params: { enabled } },
  );
  return dataOf<AutomationIssuePack>(response);
};

export const deleteHospitalAutomation = async (id: string): Promise<void> => {
  const response = await axios.delete(`/v1/hospital/automations/${encodeURIComponent(id)}`);
  dataOf<string>(response);
};

export const exportHospitalAutomation = async (
  id: string,
): Promise<AutomationExportPack> => {
  const response = await axios.get(
    `/v1/hospital/automations/${encodeURIComponent(id)}/export`,
  );
  return dataOf<AutomationExportPack>(response);
};

export const importHospitalAutomation = async (
  pack: AutomationExportPack,
): Promise<AutomationIssuePack> => {
  const response = await axios.post("/v1/hospital/automations/import", pack);
  return dataOf<AutomationIssuePack>(response);
};

export const listHospitalAutomationRuns = async (
  automationId?: string,
): Promise<AutomationRun[]> => {
  const response = await axios.get("/v1/hospital/automations/runs", {
    params: automationId ? { automationId } : undefined,
  });
  return dataOf<AutomationRun[]>(response) || [];
};

export const getHospitalAutomationRun = async (runId: string): Promise<AutomationRun> => {
  const response = await axios.get(
    `/v1/hospital/automations/runs/${encodeURIComponent(runId)}`,
  );
  return dataOf<AutomationRun>(response);
};
